import { Injectable } from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { map, take, Observable } from 'rxjs';

import { ACCOUNT_FEATURE_FLAG_TITLES, AccountFeatureFlags } from '@shared/account/constants';
import { Features } from '@shared/account/models';
import { PlanTier } from '@shared/price-plan/enums';
import { PricePlan } from '@shared/price-plan/models';
import { AuthUser } from '@shared/signup/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { UpdateBillingComponent } from '../modals';
import { PlanLimit } from '../models';

@Injectable()
export class BillingService {
  constructor(private readonly modalController: FrontendModalService) {}

  getAccountFeaturesByTier(features: Features, tier = PlanTier.free): string {
    if (tier === PlanTier.free) {
      return 'basic functional';
    }

    if (tier === PlanTier.starter) {
      return 'Analytics, Estimated waits, and 1:1 Chat';
    }

    if (tier === PlanTier.business) {
      const enabledAccountFeatures: string[] = [];

      for (const feature in features) {
        if (features[feature] && (feature === AccountFeatureFlags.togo || feature === AccountFeatureFlags.floorPlan || feature === AccountFeatureFlags.savedReplies)) {
          enabledAccountFeatures.push(ACCOUNT_FEATURE_FLAG_TITLES[feature]);
        }
      }

      const enabledAccountFeaturesString = enabledAccountFeatures.length > 0 ? enabledAccountFeatures.join(', ') + ',' : '';
      return `Resources, Services, Analytics, Geofencing, Waitlist Estimated Wait,
        ${enabledAccountFeaturesString} Custom Properties, Automations,
        Integrations, Data Exports, Online Reservations,
        and 1:1 Chat`;
    }

    return '';
  }

  prepareChangeSubscriptionPlanConfirmationText(
    pricePlan: PricePlan,
    currentPricePlan: PricePlan,
    features: Features,
    currentUsage: number,
    isSubscriptionOnPaymentTrial: boolean,
  ): string {
    const isFreePlan = pricePlan.tier === PlanTier.free;
    const chargeText = isFreePlan
      ? ''
      : `You will be charged <strong>${new CurrencyPipe('en-US').transform(pricePlan.price / 100, 'USD', 'symbol', '1.0-0')}</strong>.`;

    let featuresText = `You will get access to ${this.getAccountFeaturesByTier(features, pricePlan.tier)} features.`;

    if (currentPricePlan.tier === pricePlan.tier) {
      featuresText = '';
    }

    let paymentTime = isFreePlan || currentUsage > pricePlan.limit
      ? '<span>Your subscription plan will be changed at the end of current billing period.</span>'
      : '<span>Any time left on your current plan will be pro rated against this amount.</span>';

    if (isFreePlan && isSubscriptionOnPaymentTrial) {
      paymentTime = '';
      featuresText = `Warning: the ${this.getAccountFeaturesByTier(features, currentPricePlan.tier)} features aren't available on the Free Plan..`;
    }

    return `You are about to change your plan to <strong>${pricePlan.name}</strong>. ${featuresText}
      Your monthly message limit will be <strong>${pricePlan.limit.toLocaleString()}</strong> messages.
      ${chargeText} ${paymentTime}`;
  }

  addBillingData(user: AuthUser, recurlyApiKey: string): Observable<boolean> {
    return this.modalController.create<UpdateBillingComponent, boolean>(UpdateBillingComponent, {
      billingInfo: user.account.recurly.billing_info,
      billingEmail: user.email,
      recurlyApiKey,
      title: 'Adding Payment Info',
      user: user,
    }).pipe(
      take(1),
      map(res => res),
    );
  }

  preparePricePlanLimit(pricePlan: PricePlan): PlanLimit {
    const { limit, availableForManager, enabled, outdated } = pricePlan;

    return {
      value: limit,
      label: `${limit.toLocaleString()} SMS`,
      hidden: !availableForManager || !enabled || outdated,
    };
  }

  sortPlanLimits(limits: PlanLimit[], freePlanLimit: number): PlanLimit[] {
    return limits.sort((a, b) => a.value - b.value)
      .filter(({ value }) => value > 0 && value !== freePlanLimit);
  }
}
